import React, { useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, ChevronDown } from 'lucide-react';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import AppHeader from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import EngineerHeader from '../components/pm/EngineerHeader';
import InspirationBoard from '../components/board/InspirationBoard';
import type { BoardCommentAuthor } from '../components/board/comments/BoardCommentsContext';
import { isArchitectRole, isDesignerRole } from '../utils/userRoles';
import './InspirationBoardPage.css';

function dealLabel(deal: { projectName?: string; title?: string; name?: string }): string {
  return deal.projectName || deal.title || deal.name || 'Project';
}

function boardBackPath(opts: {
  role?: string;
  leadId?: string;
  timberProjectId?: string;
  from?: unknown;
}): string {
  const from = typeof opts.from === 'string' ? opts.from : '';
  if (
    from === '/designer-portal'
    || from === '/architect-portal'
    || from === '/engineer-portal'
    || from.startsWith('/project-hub')
  ) {
    return from;
  }
  if (isDesignerRole(opts.role) || isArchitectRole(opts.role) || opts.role === 'engineer') {
    return opts.timberProjectId ? `/project-hub/${opts.timberProjectId}` : '/project-hub';
  }
  return opts.leadId ? `/client/${opts.leadId}` : '/';
}

const InspirationBoardPage: React.FC = () => {
  const { leadId, dealId } = useParams<{ leadId: string; dealId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const focusNodeId = searchParams.get('node') || undefined;
  const { leads, enhancedDeals, timberProjects } = useCRM();
  const { user } = useAuth();

  const commentAuthor = useMemo((): BoardCommentAuthor => ({
    uid: user?.id || 'unknown',
    displayName: user?.name || user?.email || 'Team Member',
    canWrite: true,
    kind: 'staff',
  }), [user]);

  const lead = useMemo(() => leads.find((l) => l.id === leadId), [leads, leadId]);
  const deal = useMemo(
    () => enhancedDeals.find((d) => d.id === dealId),
    [enhancedDeals, dealId],
  );
  const siblingDeals = useMemo(
    () => (leadId ? enhancedDeals.filter((d) => d.leadId === leadId) : []),
    [enhancedDeals, leadId],
  );

  const leadName = lead ? `${lead.firstName} ${lead.lastName}`.trim() : '';
  const projectLabel = deal ? dealLabel(deal) : 'Project';
  const showProjectSwitcher = siblingDeals.length > 1;
  const timberProjectId = useMemo(
    () => timberProjects.find((p) => p.dealId === dealId)?.id,
    [timberProjects, dealId],
  );

  const Header = isDesignerRole(user?.role) || isArchitectRole(user?.role)
    ? ArchitectHeader
    : user?.role === 'engineer'
      ? EngineerHeader
      : AppHeader;
  const headerEl = isArchitectRole(user?.role) ? (
    <ArchitectHeader
      homePath="/architect-portal"
      homeLinkLabel="Status"
      showStandup={false}
      showHours={false}
      linkSearch={location.search.includes('previewAs=architect') ? location.search : ''}
    />
  ) : (
    <Header />
  );

  const backPath = boardBackPath({
    role: user?.role,
    leadId,
    timberProjectId,
    from: (location.state as { from?: string } | null)?.from,
  });
  const backLabel = backPath.startsWith('/architect-portal')
    ? 'Back to Status'
    : backPath.startsWith('/designer-portal')
    ? 'Back to designer portal'
    : backPath.startsWith('/engineer-portal')
      ? 'Back to engineer portal'
      : backPath.startsWith('/project-hub')
        ? 'Back to Project Hub'
        : 'Back to client workspace';

  if (!leadId || !dealId) {
    return (
      <div className="board-page">
        {headerEl}
        <div className="board-page-empty">No project specified.</div>
      </div>
    );
  }

  return (
    <div className="board-page">
      {headerEl}
      <div className="board-subbar">
        <button
          type="button"
          className="board-subbar-back"
          onClick={() => navigate(backPath)}
          aria-label={backLabel}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="board-subbar-title">
          <LayoutGrid size={16} />
          <span className="board-subbar-label">Inspiration Board</span>
          {leadName && <span className="board-subbar-lead">· {leadName}</span>}
          {showProjectSwitcher ? (
            <span className="board-subbar-project-switch">
              ·
              <select
                className="board-subbar-project-select"
                value={dealId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  if (nextId && nextId !== dealId) {
                    navigate(`/client/${leadId}/project/${nextId}/board`, {
                      state: location.state,
                    });
                  }
                }}
                aria-label="Switch project board"
              >
                {siblingDeals.map((d) => (
                  <option key={d.id} value={d.id}>
                    {dealLabel(d)}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="board-subbar-project-chevron" aria-hidden />
            </span>
          ) : (
            <span className="board-subbar-lead">· {projectLabel}</span>
          )}
        </div>
        <div className="board-subbar-hint">
          Drag in images, paste links, double-click to edit notes
        </div>
      </div>
      <div className="board-stage">
        <InspirationBoard
          dealId={dealId}
          timberProjectId={timberProjectId}
          migrateFromLeadId={leadId}
          embedded={false}
          commentAuthor={commentAuthor}
          focusNodeId={focusNodeId}
        />
      </div>
    </div>
  );
};

export default InspirationBoardPage;
